import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getProductReviews } from "../../services/products";
import { addReview } from "../../services/reviews";
import { toast } from "react-toastify";
import "../../styles/ProductReview.css";

const normUrl = (u) =>
  typeof u === "string" ? u.replace("http://", "https://") : u;

// ── onStatsLoad: lifted to ProductDetail for top-panel rating sync
const ProductReview = ({ productId, productTitle, onStatsLoad }) => {
  const { isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [overall, setOverall] = useState(0);
  const [total, setTotal] = useState(0);
  const [breakdown, setBreakdown] = useState({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
  const [hasMyReview, setHasMyReview] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: "",
    review_images: [],
  });
  const [previewImages, setPreviewImages] = useState([]);

  // ── Process raw API response ────────────────────────────────────────────────
  const processReviewData = useCallback((data) => {
    const raw = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
        ? data
        : [];

    const ratingBreakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let totalRating = 0;
    let myReviewFound = false;

    raw.forEach((r) => {
      const rating = Number(r.rating);
      if (rating >= 1 && rating <= 5) {
        ratingBreakdown[rating]++;
        totalRating += rating;
      }
      if (r.is_my_review) myReviewFound = true;
    });

    const avgRating = raw.length > 0 ? totalRating / raw.length : 0;

    const mapped = raw.map((r) => {
      // images
      let images = [];
      if (Array.isArray(r.review_images)) {
        images = r.review_images
          .map((img) => {
            if (typeof img === "string") return normUrl(img);
            if (img?.image_url) return normUrl(img.image_url);
            if (img?.url) return normUrl(img.url);
            return null;
          })
          .filter(Boolean);
      } else if (typeof r.review_images === "string") {
        images = [normUrl(r.review_images)];
      }

      // vendor reply — handles both `vendor_reply` and `reply` shapes
      let vendorReply = null;
      const vr = r.vendor_reply || r.reply;
      if (vr) {
        vendorReply = {
          shopName: vr.vendor?.store_name || vr.shop_name || "Vendor",
          shopIcon:
            normUrl(vr.vendor?.profile_image || vr.vendor_image) || "🏪",
          responseText: vr.reply || vr.response || vr.comment || "",
          responseDate: vr.created_at || vr.replied_at || null,
        };
      }

      return {
        id: r.id,
        isMyReview: !!r.is_my_review,
        rating: Number(r.rating),
        reviewerName:
          r.customer?.full_name ||
          r.customer?.name ||
          r.user?.name ||
          "Anonymous",
        reviewDate: r.created_at
          ? new Date(r.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
        reviewText: r.comment || r.review || "",
        reviewImages: images,
        shopResponse: vendorReply,
      };
    });

    // Pin the user's own review to top
    const sorted = [
      ...mapped.filter((r) => r.isMyReview),
      ...mapped.filter((r) => !r.isMyReview),
    ];

    return {
      avgRating,
      reviewsList: sorted,
      ratingBreakdown,
      totalCount: raw.length,
      myReviewFound,
    };
  }, []);

  // ── Fetch reviews ────────────────────────────────────────────────────────────
  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getProductReviews(productId);
      const {
        avgRating,
        reviewsList,
        ratingBreakdown,
        totalCount,
        myReviewFound,
      } = processReviewData(response.data);

      setOverall(avgRating);
      setTotal(totalCount);
      setBreakdown(ratingBreakdown);
      setReviews(reviewsList);
      setHasMyReview(myReviewFound);

      if (typeof onStatsLoad === "function") {
        onStatsLoad({ average: avgRating, total: totalCount });
      }
    } catch (e) {
      console.warn("[REVIEWS] fetch failed:", e);
      setOverall(0);
      setTotal(0);
      setBreakdown({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
      setReviews([]);
      setHasMyReview(false);
      if (typeof onStatsLoad === "function")
        onStatsLoad({ average: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [productId, processReviewData, onStatsLoad]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  // ── Stars ────────────────────────────────────────────────────────────────────
  const renderStars = (rating, size = 16) => {
    const full = Math.floor(Number(rating || 0));
    const hasHalf = Number(rating || 0) % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < full; i++)
      stars.push(
        <svg
          key={`f${i}`}
          className="review-star filled"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
        </svg>,
      );
    if (hasHalf && full < 5)
      stars.push(
        <svg
          key="half"
          className="review-star half-filled"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <defs>
            <linearGradient id={`halfStar${size}`}>
              <stop offset="50%" stopColor="#f77321" />
              <stop offset="50%" stopColor="#dde3ed" />
            </linearGradient>
          </defs>
          <path
            fill={`url(#halfStar${size})`}
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
          />
        </svg>,
      );
    const empty = 5 - Math.ceil(Number(rating || 0));
    for (let i = 0; i < empty; i++)
      stars.push(
        <svg
          key={`e${i}`}
          className="review-star empty"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
        </svg>,
      );
    return stars;
  };

  const rows = useMemo(
    () =>
      [5, 4, 3, 2, 1].map((star) => {
        const count = breakdown[star] || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return { star, count, pct };
      }),
    [breakdown, total],
  );

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      toast.warning("Maximum 5 images allowed");
      return;
    }
    setNewReview((prev) => ({ ...prev, review_images: files }));
    setPreviewImages(files.map((f) => URL.createObjectURL(f)));
  };

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) {
      toast.warning("Please write a comment");
      return;
    }
    try {
      setSubmitting(true);
      await addReview({
        product_id: productId,
        rating: newReview.rating,
        comment: newReview.comment,
        review_images: newReview.review_images,
      });
      toast.success("Review submitted successfully!");
      setNewReview({ rating: 5, comment: "", review_images: [] });
      setPreviewImages([]);
      setShowReviewModal(false);
      await loadReviews();
    } catch (err) {
      console.error("Review submit error:", err);
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowReviewModal(false);
    setNewReview({ rating: 5, comment: "", review_images: [] });
    setPreviewImages([]);
  };

  // Write button: only when authenticated AND no review yet
  const canWriteReview = isAuthenticated && !hasMyReview;

  return (
    <div className="review-section">
      {/* ── Rating Overview ── */}
      <div className="rating-overview">
        <div className="rating-summary">
          <div className="overall-rating">
            <span className="rating-number">{Number(overall).toFixed(1)}</span>
            <span className="rating-total">/5</span>
          </div>
          <div className="rating-stars-main">{renderStars(overall, 18)}</div>
          <div className="total-ratings">
            {total} {total === 1 ? "Rating" : "Ratings"}
          </div>
        </div>
        <div className="rating-breakdown">
          {rows.map(({ star, count, pct }) => (
            <div key={star} className="rating-breakdown-row">
              <div className="rating-stars-small">{renderStars(star, 13)}</div>
              <div className="rating-bar">
                <div className="rating-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="rating-count">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Write Review / Already Reviewed row ── */}
      <div className="review-action-row">
        {isAuthenticated && hasMyReview && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
              color: "#0e3864",
              fontWeight: 500,
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              style={{ stroke: "#0e3864" }}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>You've already reviewed this product</span>
          </div>
        )}
        {canWriteReview && (
          <button
            className="write-review-btn"
            onClick={() => setShowReviewModal(true)}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            Write a Review
          </button>
        )}
      </div>

      {/* ── Reviews list ── */}
      <div className="reviews-container">
        <h3 className="reviews-title">Reviews</h3>

        {loading && <div className="reviews-loading">Loading reviews…</div>}
        {!loading && reviews.length === 0 && (
          <div className="reviews-empty">
            No reviews yet — be the first to share your experience!
          </div>
        )}

        {reviews.map((review) => (
          <div
            key={review.id}
            className={`review-card${review.isMyReview ? " review-card--mine" : ""}`}
          >
            <div className="review-header">
              <div className="review-rating-info">
                <div className="review-rating-stars">
                  {renderStars(review.rating, 14)}
                </div>
                <div className="reviewer-name">
                  {review.reviewerName}
                  {review.isMyReview && (
                    <span className="review-mine-badge">Your Review</span>
                  )}
                </div>
              </div>
              <div className="review-date">Reviewed on {review.reviewDate}</div>
            </div>

            <div className="review-content">
              <p className="review-text">{review.reviewText}</p>
            </div>

            {review.reviewImages?.length > 0 && (
              <div className="review-images">
                {review.reviewImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Review ${idx + 1}`}
                    className="review-image"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "https://via.placeholder.com/70x70?text=img";
                    }}
                  />
                ))}
              </div>
            )}

            {review.shopResponse && (
              <div className="shop-response">
                <div className="shop-response-header">
                  <div className="shop-response-info">
                    {typeof review.shopResponse.shopIcon === "string" &&
                    review.shopResponse.shopIcon.startsWith("http") ? (
                      <img
                        src={review.shopResponse.shopIcon}
                        alt="Shop"
                        className="shop-response-icon-img"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="shop-response-icon">
                        {review.shopResponse.shopIcon}
                      </div>
                    )}
                    <div className="shop-response-details">
                      <span className="shop-response-name">
                        {review.shopResponse.shopName}
                      </span>
                      {review.shopResponse.responseDate && (
                        <span className="shop-response-date">
                          Replied on{" "}
                          {new Date(
                            review.shopResponse.responseDate,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="shop-response-text">
                  {review.shopResponse.responseText}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Write Review Modal ── */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={handleCloseModal}>
          <div
            className="review-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="review-modal-header">
              <h3>Write a Review</h3>
              <button className="review-modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <div className="review-modal-body">
              <div className="review-form-group">
                <label className="review-form-label">Your Rating</label>
                <div className="review-rating-input">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="review-star-btn"
                      onClick={() =>
                        setNewReview((prev) => ({ ...prev, rating: star }))
                      }
                    >
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill={newReview.rating >= star ? "#f77321" : "none"}
                        stroke="#f77321"
                        strokeWidth="1.5"
                      >
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                      </svg>
                    </button>
                  ))}
                  <span className="review-rating-text">
                    {newReview.rating} star{newReview.rating !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              <div className="review-form-group">
                <label className="review-form-label" htmlFor="rv-modal-comment">
                  Your Review
                </label>
                <textarea
                  id="rv-modal-comment"
                  className="review-form-textarea"
                  placeholder="Share your experience with this product…"
                  rows="5"
                  value={newReview.comment}
                  onChange={(e) =>
                    setNewReview((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="review-form-group">
                <label className="review-form-label">
                  Add Photos{" "}
                  <span style={{ opacity: 0.5, fontWeight: 400 }}>
                    (optional)
                  </span>
                </label>
                <div className="review-image-upload">
                  <input
                    type="file"
                    id="rv-modal-images"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="review-file-input"
                  />
                  <label
                    htmlFor="rv-modal-images"
                    className="review-upload-btn"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    Choose Images
                  </label>
                  <span className="review-upload-hint">Max 5 images</span>
                </div>

                {previewImages.length > 0 && (
                  <div className="review-preview-images">
                    {previewImages.map((preview, idx) => (
                      <div key={idx} className="review-preview-image">
                        <img src={preview} alt={`Preview ${idx + 1}`} />
                        <button
                          type="button"
                          className="review-remove-image"
                          onClick={() => {
                            const newFiles = Array.from(
                              newReview.review_images,
                            ).filter((_, i) => i !== idx);
                            setNewReview((prev) => ({
                              ...prev,
                              review_images: newFiles,
                            }));
                            setPreviewImages((prev) =>
                              prev.filter((_, i) => i !== idx),
                            );
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="review-modal-footer">
              <button className="review-cancel-btn" onClick={handleCloseModal}>
                Cancel
              </button>
              <button
                className="review-submit-btn"
                onClick={handleSubmitReview}
                disabled={submitting || !newReview.comment.trim()}
              >
                {submitting ? "Submitting…" : "Submit Review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductReview;
